import { useEffect, useMemo, useRef, useState } from 'react'
import { useTimeline } from '../../context/useTimeline'
import { useGamification } from '../../context/useGamification'
import { useAmbientOpportunity } from '../../hooks/useAmbientOpportunity'
import { AmbientSuggestionChip } from './AmbientSuggestionChip'
import type { AmbientOpportunity } from '../../services/ai/ambient/ambientTypes'
import { lineFeatureFlags } from '../../config/lineFeatureFlags'
import type { AiOperationTargetRef } from '../../types/aiOperational'

/**
 * Self-contained wrapper the composer mounts above the input. Handles all
 * the plumbing (workspace snapshot from context, keystroke idle tracking,
 * suppression rules, dispatching accept to a caller-supplied prompt setter)
 * so callers only need to gate on off-topic priority.
 *
 * Renders NOTHING when suppressed, when the feature flag is off, or when
 * no opportunity is currently active.
 */
export const LineComposerAmbientChip = ({
  suppressed,
  onAccept,
  onModify,
}: {
  /** True when the off-topic chip owns the slot, or when Line is thinking, or when the textarea has text. */
  suppressed: boolean
  /** Prompt setter — pre-fills Line composer when the user accepts. */
  onAccept: (prompt: string) => void
  onModify?: (prompt: string) => void
}) => {
  const { elements, checklistItems } = useTimeline()
  const gamification = useGamification()
  const ledgerCredits = gamification.ledgerCredits

  const workspaceElements = useMemo(() => elements.map((element) => ({
    id: element.id,
    parentId: element.parentId,
    type: element.type as AiOperationTargetRef['type'],
    title: element.title,
    board: element.board as AiOperationTargetRef['board'],
    updatedAtIso: typeof element.lastUpdatedAt === 'string' ? element.lastUpdatedAt : undefined,
    childCount: elements.filter((child) => child.parentId === element.id).length,
  })), [elements])

  const checklistCompletionByOwner = useMemo(() => {
    const map: Record<string, { total: number; completed: number; label: string }> = {}
    checklistItems.forEach((item) => {
      const ownerId = item.parentContainerId ?? ''
      if (!ownerId) return
      const owner = elements.find((element) => element.id === ownerId)
      const label = owner?.title ?? 'Checklist'
      const entry = map[ownerId] ?? { total: 0, completed: 0, label }
      entry.total += 1
      if (item.workflowStatus === 'COMPLETED') entry.completed += 1
      map[ownerId] = entry
    })
    return map
  }, [checklistItems, elements])

  const workspaceId = elements[0]?.workspaceId ?? ''

  // Idle-time tracking for the LLM refine gate.
  const [msSinceLastKeystroke, setMsSinceLastKeystroke] = useState(60_000)
  const lastKeystrokeRef = useRef(Date.now())
  useEffect(() => {
    const onKey = () => { lastKeystrokeRef.current = Date.now() }
    window.addEventListener('keydown', onKey)
    const interval = window.setInterval(() => {
      setMsSinceLastKeystroke(Date.now() - lastKeystrokeRef.current)
    }, 5_000)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.clearInterval(interval)
    }
  }, [])

  const { current, dismiss, accept } = useAmbientOpportunity({
    workspaceId,
    workspaceElements,
    checklistCompletionByOwner,
    suppressed,
    msSinceLastKeystroke,
    ledgerCredits,
  })

  if (!lineFeatureFlags.ambient) return null
  if (suppressed || !current) return null

  const handleAccept = (opportunity: AmbientOpportunity) => {
    accept(opportunity)
    onAccept(opportunity.primaryAction.prompt)
  }
  const handleModify = onModify
    ? (opportunity: AmbientOpportunity) => {
        accept(opportunity)
        onModify(opportunity.secondaryAction?.prompt ?? opportunity.primaryAction.prompt)
      }
    : undefined

  return (
    <AmbientSuggestionChip
      opportunity={current}
      onAccept={handleAccept}
      onModify={handleModify}
      onDismiss={dismiss}
    />
  )
}
